import _once from 'lodash/once'
import _memoize from 'lodash/memoize'
import * as SentryVue from '@sentry/vue'
import type { Router } from 'vue-router'
import { breadcrumbsIntegration } from '@sentry/browser'
import { sha512_256 } from 'js-sha512'

const DEFAULT_ERROR_SAMPLE_RATE = 0.2
const DEFAULT_TRACES_SAMPLE_RATE = 0.2

/**
 * SSE endpoint that holds the connection open — emitting an http.client span every time it
 * reconnects. v10's first-party `ignoreSpans` filter drops these by op + name, replacing the
 * hand-rolled `beforeSendSpan` we used for the same job. The previous filter returned
 * `{ ...span, sampled: false }`, which is not a valid SpanJSON shape and didn't actually drop the
 * span — `ignoreSpans` does.
 */
const IGNORE_SPANS = [
  { op: 'http.client', name: /\/api\/v1\/notifications\/unseen/ }
]

export const initSentryOnce = _once(initSentry)

const sanitizeCache = _memoize((data: string) => sha512_256(data))

const QUERY_PARAM_REGEX = /([?&])([^=&]+)=([^&]*)/g

/**
 * Replace each `?key=value` / `&key=value` pair's value with a sha512_256 hash, leaving the URL
 * path and parameter NAMES intact. Hashing (rather than dropping) preserves the ability to
 * correlate events sharing the same underlying value without revealing the value itself.
 */
export function sanitizeUrl(value: string): string {
  if (!value.includes('=') || (!value.includes('?') && !value.includes('&'))) return value
  return value.replaceAll(QUERY_PARAM_REGEX, (_match, sep: string, key: string, val: string) => `${sep}${key}=${sanitizeCache(val)}`)
}

/**
 * Apply `sanitizeUrl` to every string value in a flat record. Used on the leaf maps that Sentry
 * exposes (`tags`, `*.data`, `extra`) — these are guaranteed plain by the SDK's own
 * `normalize(depth=3)` step, so a one-level pass is enough and we never recurse into unknown
 * subtrees (where Vue 3 component-instance proxies attached by `attachProps` and the tracing
 * mixin's `$_sentryRootComponentSpanTimer` markers would trigger `ownKeys` warnings).
 */
function scrubFlatRecord(rec: Record<string, unknown> | undefined | null): void {
  if (!rec) return
  Object.keys(rec).forEach((key) => {
    const v = rec[key]
    if (typeof v === 'string') {
      // eslint-disable-next-line no-param-reassign
      rec[key] = sanitizeUrl(v)
    }
  })
}

/**
 * Hash query-parameter values out of every event field where they actually land in @sentry/vue
 * v10. We touch only well-known string-bearing slots and leaf records — never an unbounded walk
 * over the event — because:
 *   (a) Sentry's `prepareEvent` already runs `normalize(depth=3)` over `breadcrumbs[].data`,
 *       `user`, `contexts`, `extra`, `contexts.trace.data`, and `spans[].data`, so those slots are
 *       guaranteed plain and shallow by the time we see them.
 *   (b) Walking past those slots into `event.contexts.vue.propsData` or any other unmangled Vue
 *       reference would trigger Vue's `PublicInstanceProxyHandlers.ownKeys` warning and pointlessly
 *       enumerate component-instance internals.
 *
 * Earlier versions of this file deep-cloned the event and walked every key (which blew the stack
 * on Vue reactive proxies whose identity changes on every property access — that's what dropped
 * 100% of transactions); a follow-up tried a depth-capped recursive walker (which avoided the
 * stack overflow but still spammed Vue warnings on component-instance proxies in the tree).
 */
export function scrubEventUrls<T extends SentryVue.Event>(event: T): T {
  /* eslint-disable no-param-reassign */
  if (typeof event.message === 'string') {
    event.message = sanitizeUrl(event.message)
  }
  if (event.request?.url) {
    event.request.url = sanitizeUrl(event.request.url)
  }
  const referer = event.request?.headers?.Referer
  if (typeof referer === 'string' && event.request?.headers) {
    event.request.headers.Referer = sanitizeUrl(referer)
  }
  event.exception?.values?.forEach((exception) => {
    if (typeof exception.value === 'string') {
      exception.value = sanitizeUrl(exception.value)
    }
  })
  event.breadcrumbs?.forEach((breadcrumb) => {
    if (typeof breadcrumb.message === 'string') {
      breadcrumb.message = sanitizeUrl(breadcrumb.message)
    }
    scrubFlatRecord(breadcrumb.data as Record<string, unknown> | undefined)
  })
  event.spans?.forEach((span) => {
    if (typeof span.description === 'string') {
      span.description = sanitizeUrl(span.description)
    }
    scrubFlatRecord(span.data as Record<string, unknown> | undefined)
  })
  scrubFlatRecord(event.contexts?.trace?.data as Record<string, unknown> | undefined)
  scrubFlatRecord(event.tags as Record<string, unknown> | undefined)
  scrubFlatRecord(event.extra as Record<string, unknown> | undefined)
  /* eslint-enable no-param-reassign */
  return event
}

/**
 * Initialises sentry client for vue.js client.
 *
 * The `_once` wrapper above guards against repeated calls within a single module instance, but `getClient()` adds a second line of defence: webpack-dev-server HMR (or any other path that re-evaluates this module) would otherwise produce a fresh `_once` closure per evaluation and re-init Sentry. Each extra `SentryVue.init` chains another `app.config.errorHandler` wrapper, so a single Vue error fires N captureException calls — exactly the runaway loop reported alongside this fix.
 */
function initSentry(app: any, router: Router) {
  if (window.SENTRY_ENABLED && window.SENTRY_PEGASUS_URL) {
    if (SentryVue.getClient()) return
    SentryVue.init({
      app,
      attachProps: true,
      dsn: window.SENTRY_PEGASUS_URL,
      integrations: [
        SentryVue.browserTracingIntegration({ router }),
        breadcrumbsIntegration({
          console: false,
          fetch: false,
          history: false, // might cause intellectual property concerns when capturing url query params
          xhr: false // might cause intellectual property concerns when capturing url query params
        })
      ],
      sampleRate: DEFAULT_ERROR_SAMPLE_RATE,
      tracesSampleRate: DEFAULT_TRACES_SAMPLE_RATE,
      release: window.APP_VERSION,
      environment: window.location.hostname,
      sendDefaultPii: false,
      ignoreErrors: [ /Non-Error promise rejection captured with value:/, /NavigationDuplicated/ ], // fixes DEV-7098
      ignoreSpans: IGNORE_SPANS,
      beforeSend: scrubEventUrls,
      beforeSendTransaction: scrubEventUrls
    })
  }
}
